import { z } from "zod"

/* Ask for what is missing (PRD: portal-missing-statements). LedgerBox drafts
   an ask when a client's statements stop arriving; the firm sends it or
   dismisses it. The API routes and the api-client both parse through these,
   so the two can never disagree. */

const MonthSchema = z.string().regex(/^\d{4}-\d{2}$/)

export const ApiMissingStatementSignalSchema = z.enum(["hole", "overdue"])

export const ApiMissingStatementDraftStateSchema = z.enum([
  "open",
  "sent",
  "dismissed",
  "resolved",
])

/** One drafted ask, as the client's Requests tab lists it. */
export const ApiMissingStatementDraftSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  /** "Chase ending 4411". */
  accountLabel: z.string(),
  /** First missing month; equals `month` for a monthly account. */
  fromMonth: MonthSchema,
  /** The missing month, or the last month of a run. */
  month: MonthSchema,
  signal: ApiMissingStatementSignalSchema,
  state: ApiMissingStatementDraftStateSchema,
  /** The item name the client reads: "Chase ending 4411, March 2026". */
  name: z.string(),
  /** The line under the item, in the client's words. */
  clientText: z.string(),
  /**
   * Files the client already sent that sit on no item and read as a
   * statement for these months: the firm may have the statement already.
   */
  unfiledFiles: z
    .array(z.object({ id: z.string().uuid(), fileName: z.string() }))
    .default([]),
})

export type ApiMissingStatementDraft = z.infer<
  typeof ApiMissingStatementDraftSchema
>

export const ApiMissingStatementDraftListSchema = z.object({
  drafts: z.array(ApiMissingStatementDraftSchema),
  /** Asks the firm dismissed that are still missing: it can bring one back. */
  dismissed: z.array(ApiMissingStatementDraftSchema).default([]),
})

export type ApiMissingStatementDraftList = z.infer<
  typeof ApiMissingStatementDraftListSchema
>

export const ApiMissingStatementDraftResponseSchema = z.object({
  draft: ApiMissingStatementDraftSchema,
})

/** How many asks one send takes: one request with one item per ask. */
export const MISSING_STATEMENTS_SEND_LIMIT = 24

export const ApiSendMissingStatementsBodySchema = z.object({
  draftIds: z
    .array(z.string().uuid())
    .min(1)
    .max(MISSING_STATEMENTS_SEND_LIMIT),
  dueAt: z.string().datetime(),
})

export type ApiSendMissingStatementsBody = z.infer<
  typeof ApiSendMissingStatementsBodySchema
>

/** The clients hub: how many asks wait on the firm, per client. */
export const ApiMissingStatementCountsSchema = z.object({
  workspaces: z.array(
    z.object({
      workspaceId: z.string().uuid(),
      openCount: z.number().int().positive(),
    })
  ),
})

export type ApiMissingStatementCounts = z.infer<
  typeof ApiMissingStatementCountsSchema
>

/** One month of the Accounts page strip. */
export const ApiAccountMonthCellSchema = z.object({
  month: MonthSchema,
  state: z.enum(["in", "missing", "due", "not_due", "before"]),
  /** When the statement is or was due; set for due and not_due. */
  dueOn: z.string().nullable(),
  /** The months you have on each side; set for missing. */
  between: z.object({ before: MonthSchema, after: MonthSchema }).nullable(),
  /** The ask LedgerBox drafted for this month, unless it resolved. */
  draftState: z.enum(["open", "sent", "dismissed"]).nullable(),
  /** When the firm sent that ask. */
  sentAt: z.string().datetime().nullable(),
  /** An open request already asks for this month. */
  asked: z.boolean(),
})

export type ApiAccountMonthCell = z.infer<typeof ApiAccountMonthCellSchema>
